import type { Ssm5DiagramModule } from '@packages/domain/ssm5';

import { colors } from '@packages/core/styles';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { ssm5ControlStyles, ssm5TextStyles } from '../ssm5.styles';
import { Ssm5ModuleStatusChip } from './ssm5-module-status-chip';
import { Ssm5SectionTitle } from './ssm5-section-title';

const BACK_ICON_SIZE = 24;

/** Props for {@link Ssm5ModuleHeader}. */
export type Ssm5ModuleHeaderProps = {
    /** The abbreviation of the module, as its cell shows it, e.g. "TCU / ISS". */
    label: string;
    /** The module the panel shows. */
    module: Ssm5DiagramModule;
    /** The full name of the module, when one is known. */
    name?: string;
    /** Called when the back button is pressed. */
    onBack: () => void;
};

const useStyles = createUseStyles({
    // The design's header: a 16px inset, the back button 16px before the text, a border under it. The
    // 1px left border of the panel is drawn inside its width, so the left padding takes it off.
    header: {
        alignItems: 'flex-start',
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        gap: 16,
        padding: '16px 16px 16px 15px',
    },
    // The abbreviation and the status chip sit on one 32px line, 10px apart; the full name goes under them.
    title: {
        alignItems: 'center',
        display: 'flex',
        gap: 10,
        minHeight: 32,
    },
    text: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minWidth: 0,
    },
});

/**
 * The header of the module panel: the button back to the Status and DTCs list, the module's
 * abbreviation as its cell shows it with its status chip, and its full name under them when one is known.
 *
 * @param props - {@link Ssm5ModuleHeaderProps}
 * @returns the header
 */
export function Ssm5ModuleHeader({ label, module, name, onBack }: Ssm5ModuleHeaderProps) {
    const classes = useStyles();
    const { t } = useTranslation();

    return (
        <div className={classes.header}>
            <Button
                accessibilityLabel={t('ssm5:module.back')}
                onPress={onBack}
                style={ssm5ControlStyles.iconButton}
                variant="ghost-blue"
            >
                <Icon name="arrow-back" size={BACK_ICON_SIZE} />
            </Button>
            <div className={classes.text}>
                <div className={classes.title}>
                    <Ssm5SectionTitle>{label}</Ssm5SectionTitle>
                    <Ssm5ModuleStatusChip status={module.status} />
                </div>
                {name !== undefined && (
                    <Typography color="textDarkSecondary" style={ssm5TextStyles.moduleName} variant="default">
                        {name}
                    </Typography>
                )}
            </div>
        </div>
    );
}
